import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogContent,
  Stack,
  useTheme,
  alpha,
  Zoom,
} from "@mui/material";
import { Wheel } from "react-custom-roulette";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { EmojiEvents, Stars } from "@mui/icons-material";
import confetti from "canvas-confetti";
import { getAllWheels, rewardOfSpin } from "../redux/slices/wheel/thunk";
import LoadingPage from "../components/LoadingComponent";
import { useNavigate } from "react-router-dom";
import notify from "../components/notify";

export default function LuckyWheel() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const { wheels, loading } = useSelector((state) => state.wheel);

  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [showWinnerModal, setShowWinnerModal] = useState(false);

  useEffect(() => {
    if (wheels?.length === 0) {
      dispatch(getAllWheels());
    }
  }, [dispatch]);

  // 1. معالجة البيانات وتحسين عرض النص
  const data = useMemo(() => {
    if (!wheels || wheels?.length === 0) return [{ option: "..." }];
    
    return wheels?.map((item, index) => {
      const originalText = isRtl ? item?.name_ar : item?.name_en;
      
      // منطق بسيط لمحاولة تقسيم النص الطويل لسطرين باستخدام \n
      // ملاحظة: لا تظهر الـ \n بوضوح إلا إذا كانت الإعدادات تسمح بذلك
      let formattedOption = originalText;
      if (originalText.length > 15) {
        const mid = Math.floor(originalText.length / 2);
        const spaceIndex = originalText.lastIndexOf(" ", mid);
        if (spaceIndex !== -1) {
          formattedOption = originalText.substring(0, spaceIndex) + "\n" + originalText.substring(spaceIndex + 1);
        }
      }

      return {
        option: formattedOption,
        style: {
          backgroundColor:
            index % 2 === 0
              ? theme.palette.primary.main
              : theme.palette.background.paper,
          textColor: index % 2 === 0 ? "#ffffff" : theme.palette.text.primary,
        },
      };
    });
  }, [wheels, isRtl, theme]);

  const handleSpinClick = () => {
    if (!mustSpin && wheels?.length > 0) {
      const newPrizeNumber = Math.floor(Math.random() * wheels.length);
      setPrizeNumber(newPrizeNumber);
      setMustSpin(true);
    }
  };

  const onStopSpinning = async () => {
    setMustSpin(false);
    const winItem = wheels[prizeNumber];
    const dataArgs = { wheelId: winItem._id };
    try {
      await dispatch(rewardOfSpin({ data: dataArgs })).unwrap();
      
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: [theme.palette.primary.main, "#FFD700", "#ffffff"],
      });

      setShowWinnerModal(true);
    } catch (err) {
      notify('tryAgainLater', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowWinnerModal(false);
    navigate('/Rewards');
  };

  if (loading) return <LoadingPage />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.mode === "light"
            ? `radial-gradient(circle at 50% 50%, ${theme.palette.primary.light} 0%, #ffffff 100%)`
            : `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.1)} 0%, #0b0b0c 100%)`,
        display: "flex",
        alignItems: "center",
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center">
          <Box textAlign="center">
            <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, color: theme.palette.primary.main }}>
              {t("wheel.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("wheel.subtitle")}
            </Typography>
          </Box>

          <Box sx={{ position: "relative", p: 2 }}>
            {/* Arrow pointer on the RIGHT side — matches where react-custom-roulette stops the winner */}
            <Box
              sx={{
                position: "absolute",
                right: -20,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 100,
                filter: "drop-shadow(0px 4px 8px rgba(0,0,0,0.4))",
              }}
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderTop: "20px solid transparent",
                  borderBottom: "20px solid transparent",
                  borderRight: `40px solid ${theme.palette.error.main}`,
                }}
              />
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: "50%",
                background: theme.palette.mode === "light" ? "#f8f8f8" : "#222",
                boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.2)}`,
                position: "relative",
                direction: "ltr", // العجلة دائماً ltr لضبط الدوران
              }}
            >
              <Wheel
                mustStartSpinning={mustSpin}
                prizeNumber={prizeNumber}
                data={data}
                onStopSpinning={onStopSpinning}

                // --- إخفاء سهم المكتبة الافتراضي ---
                pointerProps={{ style: { display: 'none' } }}

                // --- تحسينات الخط والعرض ---
                fontSize={wheels.length > 10 ? 12 : 16}
                textDistance={70}
                perpendicularText={false}

                outerBorderColor={theme.palette.divider}
                outerBorderWidth={4}
                radiusLineColor={alpha(theme.palette.divider, 0.3)}
                radiusLineWidth={1}
                innerRadius={15}
              />

              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 60,
                  height: 60,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "50%",
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  border: `4px solid ${theme.palette.primary.main}`,
                }}
              >
                <Stars sx={{ color: theme.palette.primary.main, fontSize: 30 }} />
              </Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleSpinClick}
            disabled={mustSpin}
            sx={{
              px: 10, py: 2, borderRadius: 10, fontSize: "1.3rem",
              boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
              transition: "0.3s ease",
              "&:hover": { transform: "scale(1.05)" },
            }}
          >
            {mustSpin ? t("wheel.spinning") : t("wheel.spin_btn")}
          </Button>
        </Stack>
      </Container>

      {/* Winner Modal */}
      <Dialog
        open={showWinnerModal}
        TransitionComponent={Zoom}
        onClose={handleCloseModal}
        PaperProps={{ sx: { borderRadius: 8, p: 2, textAlign: "center", overflow: "visible" } }}
      >
        <DialogContent>
          <Box
            sx={{
              width: 100, height: 100, background: "gold",
              borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", mx: "auto", mb: 3, mt: -8,
              border: `6px solid ${theme.palette.background.paper}`, boxShadow: 3,
            }}
          >
            <EmojiEvents sx={{ fontSize: 50, color: "#fff" }} />
          </Box>
          <Typography variant="h4" fontWeight={900} gutterBottom>
            {t("wheel.congrats")}
          </Typography>
          <Box sx={{ py: 2, px: 4, borderRadius: 4, my: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px dashed ${theme.palette.primary.main}` }}>
            <Typography variant="h5" color="primary" fontWeight={800}>
              {wheels[prizeNumber] ? (isRtl ? wheels[prizeNumber].name_ar : wheels[prizeNumber].name_en) : ""}
            </Typography>
          </Box>
          <Button fullWidth variant="contained" onClick={handleCloseModal} sx={{ py: 1.5, borderRadius: 4 }}>
            {t("wheel.close")}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}